import { getIdFromShopifyFormattedId } from '@portal/shared/utils/shopify/ids';
import { StoreCode } from 'schema/storeCode';
import {
  SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53
} from '../config/base';

export interface FlavorInfo {
  title: string;
  color: string;
  fontColor: string;
}

/**
 * Flavor types for V53 products
 */
export type V53Flavor = 'original' | 'tropical' | 'berry' | 'citrus';

/**
 * Flavor types for AGZ products
 */
export type AGZFlavor = 'default' | 'chocolate' | 'chocolateMint' | 'mixedBerry';

/**
 * All possible flavor types
 */
export type ProductFlavor = V53Flavor | AGZFlavor;

/**
 * Flavor configuration for AG1 and AGZ products
 * Centralized color and title information for different product flavors
 */
export const FLAVOR_CONFIG: Record<string, FlavorInfo> = {
  tropical: {
    title: 'Tropical',
    color: '#E3BE38',
    fontColor: 'black'
  },
  citrus: {
    title: 'Citrus',
    color: '#FF942A',
    fontColor: 'black'
  },
  berry: {
    title: 'Berry',
    color: '#F265C7',
    fontColor: 'black'
  },
  chocolate: {
    title: 'Chocolate',
    color: '#B88C6E',
    fontColor: 'black'
  },
  chocolateMint: {
    title: 'Chocolate Mint',
    color: '#77C19A',
    fontColor: 'black'
  },
  mixedBerry: {
    title: 'Mixed Berry',
    color: '#E5657E',
    fontColor: 'black'
  },
  default: {
    title: 'Original',
    color: '#105C5C',
    fontColor: 'white'
  }
};

/**
 * Flavor types for AG1 and AGZ products
 */
export type FlavorType = keyof typeof FLAVOR_CONFIG;

/**
 * Gets the flavor configuration for a given variant ID
 * @param variantId The Shopify variant ID
 * @param store The store code
 * @returns The flavor configuration or null if not a special flavor
 */
export const getFlavorConfigFromVariantId = (variantId: string, store: StoreCode) => {
  if (!variantId) return undefined;

  // Extract just the ID number from Shopify's gid format if needed
  const normalizedId = getIdFromShopifyFormattedId(variantId);

  // Check AG1 original variants (both subscription and OTP)
  if (
    SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store]?.default === normalizedId ||
    SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store]?.default === normalizedId ||
    SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store]?.default === normalizedId ||
    SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store]?.default === normalizedId ||
    SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store]?.default === normalizedId ||
    SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store]?.default === normalizedId ||
    SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53[store]?.default === normalizedId
  ) {
    return FLAVOR_CONFIG.default;
  }

  // Check AG1 tropical variants (both subscription and OTP)
  if (
    SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store]?.tropical === normalizedId ||
    SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store]?.tropical === normalizedId ||
    SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store]?.tropical === normalizedId ||
    SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store]?.tropical === normalizedId ||
    SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store]?.tropical === normalizedId ||
    SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store]?.tropical === normalizedId ||
    SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53[store]?.tropical === normalizedId
  ) {
    return FLAVOR_CONFIG.tropical;
  }

  // Check AG1 citrus variants (both subscription and OTP)
  if (
    SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store]?.citrus === normalizedId ||
    SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store]?.citrus === normalizedId ||
    SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store]?.citrus === normalizedId ||
    SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store]?.citrus === normalizedId ||
    SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store]?.citrus === normalizedId ||
    SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store]?.citrus === normalizedId ||
    SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53[store]?.citrus === normalizedId
  ) {
    return FLAVOR_CONFIG.citrus;
  }

  // Check AG1 berry variants (both subscription and OTP)
  if (
    SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53[store]?.berry === normalizedId ||
    SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53[store]?.berry === normalizedId ||
    SHOPIFY_AG1_POUCH_OTP_VARIANT_ID_V53[store]?.berry === normalizedId ||
    SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53[store]?.berry === normalizedId ||
    SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53[store]?.berry === normalizedId ||
    SHOPIFY_TRAVEL_PACK_OTP_VARIANT_ID_V53[store]?.berry === normalizedId ||
    SHOPIFY_5CT_TRAVEL_PACK_VARIANT_ID_V53[store]?.berry === normalizedId
  ) {
    return FLAVOR_CONFIG.berry;
  }

  // Check AGZ chocolate variants
  if (SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]?.chocolate === normalizedId) {
    return FLAVOR_CONFIG.chocolate;
  }

  // Check AGZ chocolate mint variants
  if (SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]?.chocolateMint === normalizedId) {
    return FLAVOR_CONFIG.chocolateMint;
  }

  // Check AGZ mixed berry variants
  if (SHOPIFY_AGZ_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID[store]?.mixedBerry === normalizedId) {
    return FLAVOR_CONFIG.mixedBerry;
  }

  return undefined;
};
